import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  DragEvent,
  FC,
  FormEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

interface CardImportModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (file: File) => Promise<void>;
}

const CardImportModal: FC<CardImportModalProps> = ({
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(undefined);
      setIsDragOver(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const setUploadFile = (file?: File) => {
    setSelectedFile(file);
  };

  const handleSelectedFiles = (files?: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    setUploadFile(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    setIsDragOver(false);
    handleSelectedFiles(event.dataTransfer.files);
  };

  const handleDropzoneKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isSubmitting) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedFile) {
      return;
    }

    await onSubmit(selectedFile);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth="sm"
      onClose={(_event, reason) => {
        if (
          isSubmitting &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        ) {
          return;
        }

        onClose();
      }}
    >
      {isSubmitting ? <LinearProgress /> : null}

      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", letterSpacing: "0.14em" }}
          >
            New import
          </Typography>
          <Typography id="card-import-modal-title" variant="h5">
            Import collection CSV
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ display: "grid", gap: 2.5 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              CSV file
            </Typography>
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept=".csv,text/csv"
              disabled={isSubmitting}
              onChange={(event) => handleSelectedFiles(event.target.files)}
            />
            <Box
              role="button"
              tabIndex={isSubmitting ? -1 : 0}
              onClick={isSubmitting ? undefined : openFilePicker}
              onKeyDown={handleDropzoneKeyDown}
              onDragEnter={(event) => {
                event.preventDefault();
                if (!isSubmitting) {
                  setIsDragOver(true);
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (!isSubmitting) {
                  setIsDragOver(true);
                }
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDragOver(false);
              }}
              onDrop={handleDrop}
              sx={(theme) => ({
                mt: 1,
                p: 3,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: isDragOver
                  ? theme.palette.primary.main
                  : alpha(theme.palette.divider, 0.65),
                backgroundColor: isDragOver
                  ? alpha(
                      theme.palette.primary.main,
                      theme.palette.mode === "dark" ? 0.12 : 0.06,
                    )
                  : alpha(
                      theme.palette.background.default,
                      theme.palette.mode === "dark" ? 0.3 : 0.55,
                    ),
                transition:
                  "border-color 120ms ease, background-color 120ms ease, transform 120ms ease",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                "&:hover": isSubmitting
                  ? undefined
                  : {
                      transform: "translateY(-1px)",
                      borderColor: theme.palette.primary.main,
                    },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              })}
            >
              <Stack spacing={1.5} alignItems="flex-start">
                <Typography variant="subtitle1" fontWeight={700}>
                  Drag and drop a CSV here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Or click to browse from your computer.
                </Typography>
                <Button
                  variant="outlined"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openFilePicker();
                  }}
                  disabled={isSubmitting}
                >
                  Choose file
                </Button>
              </Stack>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {selectedFile
                ? `Selected: ${selectedFile.name}`
                : "Choose a ManaBox CSV from your computer or drop it here."}
            </Typography>
          </Box>

          {selectedFile ? (
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFile.type || "text/csv"}
                    {selectedFile.size > 0
                      ? ` • ${(selectedFile.size / 1024).toFixed(1)} KB`
                      : ""}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !selectedFile}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {isSubmitting ? "Importing..." : "Start import"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CardImportModal;
